import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import {
  selectPageIndex,
  selectPages,
  selectSearchResultCount,
} from '../../state/results/search-results/search-results.selectors';
import { combineLatest, Subscription } from 'rxjs';
import {
  selectIsLoadingSearchResults,
  selectIsMapVisible,
} from '@immosky/object-search-layout';
import { I18nService, PrivacyStateService } from '@immosky/shared';
import {
  selectFilterState,
  selectIsAnyFilterActive,
} from '@immosky/object-search-filter';
import { AppState, Card, FilterState, SearchResultPage } from '@immosky/types';
import { setPageIndex } from '../../state/results/search-results/search-results.actions';

@Component({
  selector: 'app-search-result-list',
  templateUrl: './search-result-list.component.html',
  styleUrls: ['./search-result-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchResultListComponent implements OnDestroy {
  private subscriptions: Subscription[] = [];

  canLoadMap = false;
  isMapVisible = false;
  isLoading = false;
  resultCount$ = this.store.pipe(select(selectSearchResultCount));

  pages: SearchResultPage[] = [];
  pageIndex = 0;
  currentPage: SearchResultPage = { cards: [] };

  isAnyFilterActive = false;
  filterState!: FilterState;

  constructor(
    private cdRef: ChangeDetectorRef,
    private store: Store<AppState>,
    private i18nService: I18nService,
    private privacyStateService: PrivacyStateService
  ) {
    const privacyStateSubscription =
      this.privacyStateService.canLoadMap.subscribe((canLoadMap: boolean) => {
        this.canLoadMap = canLoadMap;
        this.cdRef.markForCheck();
      });

    const isMapVisibleSubscription = this.store
      .pipe(select(selectIsMapVisible))
      .subscribe((isMapVisible: boolean) => {
        this.isMapVisible = isMapVisible;
        this.cdRef.markForCheck();
      });

    const resultStateSubscription = combineLatest([
      this.store.pipe(select(selectPages)),
      this.store.pipe(select(selectPageIndex)),
    ]).subscribe(([pages, pageIndex]) => {
      this.pageIndex = pageIndex;
      this.pages = pages;

      if (this.pages.length === 0) {
        this.currentPage = {
          cards: [],
        };
      } else {
        this.currentPage = this.pages[this.pageIndex];
      }

      this.cdRef.markForCheck();
    });

    const filterActiveSubscription = this.store
      .pipe(select(selectIsAnyFilterActive))
      .subscribe((isAnyFilterActive: boolean) => {
        this.isAnyFilterActive = isAnyFilterActive;
        this.cdRef.markForCheck();
      });

    const filterStateSubscription = this.store
      .pipe(select(selectFilterState))
      .subscribe((filterState: FilterState) => {
        this.filterState = filterState;
        this.cdRef.markForCheck();
      });

    const isLoadingSubscription = this.store
      .pipe(select(selectIsLoadingSearchResults))
      .subscribe((isLoading: boolean) => {
        this.isLoading = isLoading;
        this.cdRef.markForCheck();
      });

    this.subscriptions.push(
      privacyStateSubscription,
      isMapVisibleSubscription,
      filterActiveSubscription,
      filterStateSubscription,
      isLoadingSubscription,
      resultStateSubscription
    );
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  onPageChange(pageIndex: number) {
    this.store.dispatch(
      setPageIndex({
        pageIndex,
      })
    );
    this.scrollToTop();
  }

  private scrollToTop() {
    window.scrollTo({
      top: 0,
      left: 0,
    });
  }

  trackBy(index: number, item: Card): string {
    return item.id;
  }

  get title(): string {
    const categoryFilter = this.filterState.category.main;
    const parts = [
      this.i18nService.transform(
        `filters.common.title_${this.filterState.contractType.filter.value}.${
          categoryFilter.isActive ? categoryFilter.filter.value : 'none'
        }`
      ),
    ];
    const regionFilter = this.filterState.region;
    if (regionFilter.regions.length === 1) {
      parts.push(
        this.i18nService.transform(`filters.common.in`),
        regionFilter.regions[0].name
      );
    }

    return parts.join(' ');
  }
}
